import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select'
import { Switch } from '@shared/components/ui/switch'
import { Textarea } from '@shared/components/ui/textarea'
import { toast } from '@shared/hooks/use-toast'
import { apiUrl } from '@/lib/api'
import { PageHeader } from '@shared/components/ui/page-header'

interface Plan {
  id: string
  name: string
  description: string
  priceMonthly?: number
  priceAnnual?: number
  features: string[] | Record<string, boolean>
  is_active: boolean
  hidden: boolean
}

const PlanEditPage: React.FC = () => {
  const { planId } = useParams<{ planId: string }>()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [plan, setPlan] = useState<Plan | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState(0)
  const [priceMonthly, setPriceMonthly] = useState(0)
  const [priceAnnual, setPriceAnnual] = useState(0)
  const [isActive, setIsActive] = useState(true)
  const [hidden, setHidden] = useState(false)

  // normalized defaults map
  const [defaults, setDefaults] = useState<Record<string, boolean>>({})
  const [registry, setRegistry] = useState<Array<{ key: string; name: string; limitsSchema?: Record<string, { type: string; description?: string }> }>>([])
  const [featureLimits, setFeatureLimits] = useState<Record<string, Record<string, any>>>({})

  const [marketingBullets, setMarketingBullets] = useState<string[]>([])
  const [newBullet, setNewBullet] = useState('')

  const load = async () => {
    if (!planId) return
    setLoading(true)
    try {
      const { auth } = await import('@/lib/firebase')
      const idToken = await auth.currentUser?.getIdToken()
      const res = await fetch(apiUrl('/api/admin/plans'), { headers: idToken ? { 'Authorization': `Bearer ${idToken}` } : {} })
      if (!res.ok) throw new Error('Failed to load plans')
      const data = await res.json()
      const p = (data.plans || []).find((pl: any) => pl.id === planId)
      if (!p) throw new Error('Plan not found')
      setPlan(p)
      setName(p.name || '')
      setDescription(p.description || '')
      setPriceMonthly((p as any).priceMonthly || (p as any).price_monthly || 0)
      setPriceAnnual((p as any).priceAnnual || (p as any).price_annual || 0)
      setIsActive(Boolean(p.is_active))
      setHidden(Boolean(p.hidden))
      // normalize defaults
      const d: Record<string, boolean> = {}
      if (Array.isArray(p.features)) p.features.forEach((k: string) => { d[k] = true })
      else if (p.features && typeof p.features === 'object') Object.entries(p.features).forEach(([k, v]) => { d[k] = Boolean(v) })
      setDefaults(d)

      // Load feature limits from plan data
      const limits: Record<string, Record<string, any>> = {};
      if ((p as any).feature_limits && typeof (p as any).feature_limits === 'object') {
        for (const [featureKey, featureData] of Object.entries((p as any).feature_limits)) {
          if (featureData && typeof featureData === 'object' && 'limits' in featureData) {
            limits[featureKey] = featureData.limits || {};
          }
        }
      }
      setFeatureLimits(limits);

      setMarketingBullets(Array.isArray((p as any).marketing_bullets) ? (p as any).marketing_bullets : [])
      // load feature registry
      try {
        const r = await fetch(apiUrl('/api/admin/features/registry'), { headers: idToken ? { 'Authorization': `Bearer ${idToken}` } : {} })
        if (r.ok) {
          const j = await r.json()
          setRegistry((j.features || []).map((f: any) => ({ key: f.key, name: f.name, limitsSchema: f.limitsSchema || {} })))
        }
      } catch (error) {
        console.warn('Failed to load features registry:', error)
      }
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to load plan', variant: 'destructive' })
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [planId])



  const save = async () => {
    if (!planId) return
    setSaving(true)
    try {
      // features payload from checkboxes only (source of truth)
      const featuresPayload: Record<string, boolean> = { ...defaults }
      // Map UI interval to API interval ('month'|'year')
      // Update plan core fields (dual pricing)
      const { auth } = await import('@/lib/firebase')
      const idToken = await auth.currentUser?.getIdToken()
      const putRes = await fetch(apiUrl(`/api/admin/plans/${planId}`), { method: 'PUT', headers: { 'Content-Type': 'application/json', ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}) }, body: JSON.stringify({ name, description, priceMonthly, priceAnnual, features: featuresPayload, marketing_bullets: marketingBullets, feature_limits: featureLimits, hidden }) })
      if (!putRes.ok) throw new Error('Failed to update plan')
      // Toggle status if changed
      if (plan && isActive !== plan.is_active) {
        const res = await fetch(apiUrl(`/api/admin/plans/${planId}/status`), { method: 'POST', headers: { 'Content-Type': 'application/json', ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}) }, body: JSON.stringify({ isActive }) })
        if (!res.ok) throw new Error('Failed to update status')
      }
      toast({ title: 'Saved', description: 'Plan updated successfully.' })
      navigate('/plans')
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to save', variant: 'destructive' })
    } finally { setSaving(false) }
  }

  if (loading) return <div className="p-6">Loading...</div>
  if (!plan) return <div className="p-6">Plan not found</div>

  return (
    <div className="space-y-6">
      <PageHeader title={`Edit Plan: ${plan.name}`} description={`Plan ID: ${planId}`}>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/plans')}>Back</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </div>
      </PageHeader>

      <Card>
        <CardHeader><CardTitle>Details</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Monthly Price (USD)</Label>
              <Input type="number" value={priceMonthly} onChange={(e) => setPriceMonthly(parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <Label>Annual Price (USD)</Label>
              <Input type="number" value={priceAnnual} onChange={(e) => setPriceAnnual(parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <Label>Status</Label>
              <div className="mt-2 flex items-center gap-2">
                <Switch checked={isActive} onCheckedChange={setIsActive} />
                <span className="text-sm">Active</span>
              </div>
            </div>
            <div>
              <Label>Visibility</Label>
              <div className="mt-2 flex items-center gap-2">
                <Switch checked={hidden} onCheckedChange={setHidden} />
                <span className="text-sm">Hidden (Admin-only assignable)</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Hidden plans are not publicly selectable but can be assigned by admins
              </p>
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Stripe Sync</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={async () => {
              if (!planId) return; 
              try {
                const { auth } = await import('@/lib/firebase')
                const tok = await auth.currentUser?.getIdToken()
                const res = await fetch(apiUrl(`/api/admin/plans/${planId}/stripe-sync`), { method: 'POST', headers: tok ? { 'Authorization': `Bearer ${tok}` } : {} })
                if (!res.ok) throw new Error('Stripe sync failed')
                const j = await res.json()
                toast({ title: 'Stripe Sync', description: `Product and prices synced. Monthly: ${j.monthlyPriceId}, Annual: ${j.annualPriceId}` })
              } catch (e: any) {
                toast({ title: 'Stripe Sync Failed', description: e.message || 'Unknown error', variant: 'destructive' })
              }
            }}>Sync to Stripe</Button>
            <span className="text-xs text-muted-foreground">Creates/updates Stripe Product and Prices based on the amounts above.</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Features</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {registry.map(({ key, name }) => (
              <label key={key} className="flex items-center gap-2 border border-border rounded p-2">
                <input type="checkbox" checked={!!defaults[key]} onChange={(e) => setDefaults(prev => ({ ...prev, [key]: e.target.checked }))} />
                <span className="capitalize">{name || key}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Feature Limits</CardTitle></CardHeader>
        <CardContent>
          <div className="grid gap-3">
            {registry.filter(r => r.limitsSchema && Object.keys(r.limitsSchema).length > 0).map(({ key, name, limitsSchema }) => (
              <div key={key} className="border border-border rounded p-2">
                <div className="font-medium text-sm mb-1">{name || key}</div>
                <div className="grid grid-cols-1 gap-2">
                  {Object.entries(limitsSchema || {}).map(([limitKey, meta]) => (
                    <div key={limitKey} className="text-xs">
                      <Label className="text-xs">{limitKey}</Label>
                      <Input
                        className="mt-1 bg-background text-foreground"
                        placeholder={meta.description || limitKey}
                        value={String((featureLimits[key]?.[limitKey] ?? ''))}
                        onChange={(e) => {
                          const v = e.target.value
                          setFeatureLimits(prev => ({
                            ...prev,
                            [key]: {
                              ...(prev[key] || {}),
                              [limitKey]: meta.type === 'number' ? (v === '' ? null : Number(v)) : (v === '' ? null : v)
                            }
                          }))
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Marketing Bullets (non-functional perks)</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {marketingBullets.map((b, i) => (
              <div key={`${b}-${i}`} className="flex items-center gap-2">
                <Input value={b} onChange={(e) => {
                  const v = e.target.value; setMarketingBullets(prev => prev.map((x, idx) => idx === i ? v : x))
                }} />
                <Button variant="outline" size="sm" onClick={() => setMarketingBullets(prev => prev.filter((_, idx) => idx !== i))}>Remove</Button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Input placeholder="Add bullet (e.g., 24/7 Support)" value={newBullet} onChange={(e) => setNewBullet(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && newBullet.trim()) { setMarketingBullets(prev => [...prev, newBullet.trim()]); setNewBullet('') } }} />
              <Button size="sm" onClick={() => { if (newBullet.trim()) { setMarketingBullets(prev => [...prev, newBullet.trim()]); setNewBullet('') } }}>Add</Button>
            </div>
            <p className="text-xs text-muted-foreground">These are descriptive only and do not affect access. Feature access is controlled by the Feature Defaults above.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default PlanEditPage
